import React from 'react';
import { Card } from '../Card';

interface GeneralSettingsProps {
  spreadsheetId: string;
  setSpreadsheetId: (value: string) => void;
  syncFrequency: string;
  setSyncFrequency: (value: string) => void;
  autoSync: boolean;
  setAutoSync: (value: boolean) => void;
  useWeightedValues: boolean;
  setUseWeightedValues: (value: boolean) => void;
  testing: boolean;
  syncing: boolean;
  connectionStatus: 'idle' | 'success' | 'error';
  lastSync: any;
  onTestConnection: () => void;
  onSyncNow: () => void;
}

export const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  spreadsheetId,
  setSpreadsheetId,
  syncFrequency,
  setSyncFrequency,
  autoSync,
  setAutoSync,
  useWeightedValues,
  setUseWeightedValues,
  testing,
  syncing,
  connectionStatus,
  lastSync,
  onTestConnection,
  onSyncNow,
}) => {
  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'success':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-gray-400';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'success':
        return 'Connected';
      case 'error':
        return 'Not Connected';
      default:
        return 'Not Tested';
    }
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Google Sheets Configuration */}
      <Card>
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <span className="mr-2">Google Sheets Configuration</span>
        </h2>

        <div className="space-y-4">
          {/* Spreadsheet ID */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Spreadsheet ID
            </label>
            <input
              type="text"
              value={spreadsheetId}
              onChange={(e) => setSpreadsheetId(e.target.value)}
              placeholder="1YN1umiHaGb_UBFqm_pcyuCu2mA5ynYqudJ20p47Sc7Q"
              className="w-full bg-gray-900 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Found in the Google Sheets URL: docs.google.com/spreadsheets/d/<strong>SPREADSHEET_ID</strong>/edit
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onTestConnection}
              disabled={testing || !spreadsheetId.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded font-medium transition"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>

            {spreadsheetId && (
              <a
                href={`https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded font-medium transition"
              >
                View Sheet
              </a>
            )}
          </div>

          {/* Connection Status */}
          <div className={`text-sm font-medium ${getConnectionStatusColor()}`}>
            Status: {getConnectionStatusText()}
          </div>
        </div>
      </Card>

      {/* Sync Settings */}
      <Card>
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <span className="mr-2">Sync Settings</span>
        </h2>

        <div className="space-y-4">
          {/* Sync Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sync Frequency
            </label>
            <select
              value={syncFrequency}
              onChange={(e) => setSyncFrequency(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="5">Every 5 minutes</option>
              <option value="15">Every 15 minutes</option>
              <option value="30">Every 30 minutes</option>
              <option value="60">Every 60 minutes</option>
            </select>
          </div>

          {/* Auto Sync Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Automatic Sync
              </label>
              <p className="text-xs text-gray-500">
                Automatically sync data from Google Sheets at the specified frequency
              </p>
            </div>
            <button
              onClick={() => setAutoSync(!autoSync)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                autoSync ? 'bg-blue-600' : 'bg-gray-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoSync ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Sync Now Button */}
          <div>
            <button
              onClick={onSyncNow}
              disabled={syncing}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded font-medium transition"
            >
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>

          {/* Last Sync Info */}
          {lastSync && (
            <div className="text-sm text-gray-400 bg-gray-900 rounded p-3">
              <div>
                <span className="font-medium">Last sync:</span> {formatTimestamp(lastSync.completedAt)}
              </div>
              <div>
                <span className="font-medium">Records processed:</span> {lastSync.recordsProcessed}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Display Settings */}
      <Card>
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <span className="mr-2">Display Settings</span>
        </h2>

        <div className="space-y-4">
          {/* Use Weighted Values Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Use Weighted Values
              </label>
              <p className="text-xs text-gray-500">
                Apply confidence-based weighting to pipeline values (HIGH=100%, MEDIUM=50%, LOW=25%)
              </p>
            </div>
            <button
              onClick={() => setUseWeightedValues(!useWeightedValues)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                useWeightedValues ? 'bg-blue-600' : 'bg-gray-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  useWeightedValues ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};
